import { FormField } from 'sos-common/js/commonTypes';
import { FieldName } from './PublicForm.types';
import { isDateValid } from 'sos-common/js/utils/date';
import { isSamordningsnummerValid } from 'sos-common/js/utils/samordningsnummer';

enum ERROR {
  EMAIL_NOT_MATCHING = 'error_email_not_matching',
  WRONG_EMAIL_ADDRESS = 'error_wrong_email_address',
  EMAIL_DISALLOWED_CHARS = 'error_email_disallowed_chars',
  MANDATORY = 'error_mandatory',
  MANDATORYPersonnr = 'error_mandatory_personnr',
  MAX_LENGTH = 'error_max_length',
  INVALID_PERSONNUMMER = 'error_invalid_personnummer',
  INVALID_PERSONNUMMER_DATE = 'error_correct_date',
  INVALID_SAMORDNINGSNUMMER = 'error_invalid_samordningsnummer',
  INVALID_SAMORDNINGSNRDAY = 'error_invalid_samordningsnummer_day',
  FUTURE_DATE = 'error_no_future_date',
  OLD_DATE = 'error_invalid_century',
  ONLY_NUMBERS_ALLOWED = 'error_only_numbers_allowed',
  ONLY_WHOLE_OR_DOUBLES_ALLOWED = 'error_whole_or_double_allowed',
  INVALID_PHONE_NUMBER = 'error_invalid_phone_number',
  CANT_BE_ZERO = 'cant_be_zero',
  DATE_FORMAT = 'error_date_format',
  CORRECT_DATE = 'error_correct_date',
  NO_FUTURE_DATE = 'error_no_future_date',
  NO_FUTURE_YEAR = 'error_no_future_year',
  INVALID_CENTURY = 'error_invalid_century',
  NUMBER_EXCEEDING_MAX_99 = 'error_number_exceeding_max_99',
  MAX_LENGTH_5 = 'error_max5',
  YEAR_FORMAT = 'error_year_format',
  GRADUATION_BEFORE_START = 'error_graduation_before_start',
}

export const MAX_LENGTH_NUM = 255;
const EMAIL_PATTERN =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zåäöA-ZÅÄÖ\-0-9]+\.)+[a-zåäöA-ZÅÄÖ]{2,}))$/;
const disallowedChars = ['å', 'Å', 'ä', 'Ä', 'ö', 'Ö'];
const samorningsNumberAddDays = 60;
export function validateField(field: FormField<any>): string {
  if (field.isRequired && noValue(field.value)) {
    if (field.fieldName === FieldName.Personnummer || field.fieldName === FieldName.samordningsnummerOrBirthDate)
      return ERROR.MANDATORYPersonnr;

    return ERROR.MANDATORY;
  }

  if (!noValue(field.value)) {
    switch (field.fieldName) {
      case FieldName.AngeSpecialistLandController:
      case FieldName.AngeSpecialistLandController_2:
      case FieldName.AngeLandController:
        return validateCountryLength(field);
      case FieldName.Epost:
        return validateEmail(field);
      case FieldName.EpostBekraftelse:
        return validateConfirmEmail(field);
      case FieldName.Personnummer:
        return validatePersonnummer(field);
      case FieldName.samordningsnummerOrBirthDate:
        return validateSamordningsnummer(field);
      case FieldName.Postnummer:
        return validateOnlyNumbers(field);
      case FieldName.Utbildningstid:
        return validateUtbildningstid(field, 99);
      case FieldName.Examensar:
        return validateCentury(field);
      case FieldName.Telefon:
        return validatePhoneNumber(field);
      case FieldName.LegitimeradFran:
      case FieldName.DatumSpecialist:
      case FieldName.DatumSpecialist_2:
      case FieldName.SpecialExamensDatum:
      case FieldName.SpecialDatum:
      case FieldName.PaymentDate:
        return validateDateFormat(field);
      case FieldName.GranskningStartAr:
        return validateFourDigitYear(field);
      case FieldName.GranskningExamensAr:
        return validateFourDigitYear(field) || validateYearOrder(field);
    }
  }

  // return empty string if everything is valid
  return '';
}

function noValue(value: any) {
  return value === '' || value === undefined || value === null || value?.length === 0;
}

function validateCountryLength(countries: FormField<string[]>): string {
  if (countries.value.length > 5) return ERROR.MAX_LENGTH_5;
  return '';
}
function validateEmail(email: FormField<string>): string {
  if (!EMAIL_PATTERN.test(email.value)) return ERROR.WRONG_EMAIL_ADDRESS;
  let foundDisallowedChars = false;
  disallowedChars.forEach((char) => {
    if (email.value.includes(char)) foundDisallowedChars = true;
  });
  if (foundDisallowedChars) return ERROR.EMAIL_DISALLOWED_CHARS;
  return '';
}

function validateConfirmEmail(email: FormField<string>): string {
  if (email.value && email.value !== email.referenceValue) return ERROR.EMAIL_NOT_MATCHING;
  return '';
}

function validateSamordningsnummer(field: FormField<string>): string {
  // format should match: ÅÅÅÅMMDD or ÅÅÅÅMMDD-XXXX where dd in samordningsnummer should be day+ 60.
  let isValid = field.value?.match(/^\d{8}-\d{4}$|^\d{8}$/);
  if (!isValid) return ERROR.INVALID_SAMORDNINGSNUMMER;

  const year = field.value.slice(0, 4);
  const month = field.value.slice(4, 6);
  const day = field.value.slice(6, 8);

  if (!isSamordningsnummerValid(year, month, day)) return ERROR.INVALID_SAMORDNINGSNUMMER;

  const parsedDay = parseInt(day);
  const correctDateForSamordningsNr = parsedDay - samorningsNumberAddDays;

  if (!field.value?.match(/^\d{8}-\d{4}$/)) {
    if (!isDateValid(year, month, day)) return ERROR.INVALID_PERSONNUMMER_DATE;
  } else {
    if (!isDateValid(year, month, correctDateForSamordningsNr.toString())) return ERROR.INVALID_SAMORDNINGSNRDAY;
  }
  const date = field.value?.match(/^\d{8}-\d{4}$/)
    ? `${year}-${month}-${correctDateForSamordningsNr}`
    : `${year}-${month}-${day}`;
  if (Date.parse(date) > Date.now()) return ERROR.FUTURE_DATE;
  if (parseInt(year) < 1900) return ERROR.OLD_DATE;

  return '';
}

function validatePersonnummer(field: FormField<string>): string {
  // format should match: ÅÅÅÅMMDD or ÅÅÅÅMMDD-XXXX
  let isValid = field.value?.match(/^\d{8}-\d{4}$/);
  if (!isValid) return ERROR.INVALID_PERSONNUMMER;

  const year = field.value.slice(0, 4);
  const month = field.value.slice(4, 6);
  const day = field.value.slice(6, 8);

  if (!isDateValid(year, month, day)) return ERROR.INVALID_PERSONNUMMER_DATE;

  const date = `${year}-${month}-${day}`;

  if (Date.parse(date) > Date.now()) return ERROR.FUTURE_DATE;
  if (parseInt(year) < 1900) return ERROR.OLD_DATE;

  return '';
}

function validateDoubleOrWholeNumber(value: string) {
  // should match: digits, ignoring whitespace
  const isValid = value?.replace(/\s/g, '').match(/^\d*(\.\d)?$/);
  if (!isValid) return ERROR.ONLY_WHOLE_OR_DOUBLES_ALLOWED;
  return '';
}

function validateOnlyNumbers(field: FormField<string>) {
  // should match: digits, ignoring whitespace
  const isValid = field.value?.replace(/\s/g, '').match(/^\d+$/);
  if (!isValid) return ERROR.ONLY_NUMBERS_ALLOWED;
  return '';
}

function validatePhoneNumber(field: FormField<string>) {
  // should match: optional leading +, followed by (, space, digits, ) or -, 6 - 26 characters long
  const isValid = field.value?.match(/(^\+?[(| |\d|)|-]{6,26})$/);
  if (!isValid) return ERROR.INVALID_PHONE_NUMBER;
  return '';
}

function validateCentury(field: FormField<string>) {
  const { value } = field;
  const onlyNumbersError = validateOnlyNumbers(field);
  if (onlyNumbersError) return onlyNumbersError;

  const pattern = /^(19|20)\d\d$/;
  if (!pattern.test(value)) return ERROR.INVALID_CENTURY;

  //can't be higher than today
  const currentYear = new Date().getFullYear();
  if (parseInt(value) > currentYear) return ERROR.NO_FUTURE_YEAR;

  return '';
}

function validateDateFormat(field: FormField<string>) {
  const { value } = field;

  //validate format

  const pattern = /^\d\d\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
  if (!pattern.test(value)) return ERROR.DATE_FORMAT;

  //validate date
  const valueSplit = value.split('-');
  if (!isDateValid(valueSplit[0], valueSplit[1], valueSplit[2])) return ERROR.CORRECT_DATE;

  //can't be higher than today
  if (Date.parse(value) > Date.now()) return ERROR.NO_FUTURE_DATE;

  var enteredYear = parseInt(valueSplit[0], 10);
  if (enteredYear < 1900) return ERROR.OLD_DATE;

  return '';
}

function validateFourDigitYear(field: FormField<string>): string {
  if (!/^\d{4}$/.test(field.value)) return ERROR.YEAR_FORMAT;
  return '';
}

// referenceValue holds the start year
function validateYearOrder(field: FormField<string>): string {
  const start = parseInt(field.referenceValue, 10);
  const graduation = parseInt(field.value, 10);
  if (isNaN(start) || isNaN(graduation)) return '';
  return graduation >= start ? '' : ERROR.GRADUATION_BEFORE_START;
}

function validateUtbildningstid(field: FormField<string>, max: number) {
  const value = field?.value.replace(',', '.');

  const onlyNumbersInvalid = validateDoubleOrWholeNumber(value);
  if (onlyNumbersInvalid) return onlyNumbersInvalid;
  const num = Number.parseFloat(value.replace(' ', ''));
  if (num > max) return ERROR.NUMBER_EXCEEDING_MAX_99;
  if (num === 0) return ERROR.CANT_BE_ZERO;
  return '';
}
